/* eslint-disable no-unused-vars */
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Input,
  Button,
  Upload,
  message,
  Icon,
} from 'antd';
import SimpleReactValidator from 'simple-react-validator';
import {
  axiosAuthInstance,
  axiosEmptyInstance,
  API_END_POINTS,
} from 'api';
import { NOTIFICATION } from 'store/actionTypes';
import { notificationActions } from 'store/actions';
import { MESSAGES } from 'appconstants';
import {
  SpH5,
  SpError,
  SpText,
} from 'components/DesignKit';
import Previewicon from 'components/NavIcons/previewIcon';
import Uploadicon from 'components/NavIcons/uploadIcon';
import imageOnError from 'utils/imageOnError';

type Props = {
  customizeInfo: Object,
  brandingInfo: Object,
  setNotification: Function,
};

const Customize = forwardRef((props: Props, ref) => {
  const {
    customizeInfo,
    brandingInfo,
    setNotification,
  } = props;

  const [, forceUpdate] = useState();
  const [heroText, setHeroText] = useState(customizeInfo.heroText);
  const [subText, setSubText] = useState(customizeInfo.subText);
  const [btnText, setBtnText] = useState(customizeInfo.btnText);
  const [bgLogo, setBgLogo] = useState(customizeInfo.bgLogo);
  const [loader, setLoading] = useState(false);
  const [preview, setPreview] = useState(brandingInfo.logo ? (
    // eslint-disable-next-line max-len
    <img src={`${customizeInfo.bgLogo}?time=${new Date().getTime()}`} alt="" onError={event => imageOnError(event)} style={{ width: '100%' }} />) : '');
  // eslint-disable-next-line no-unused-vars
  const [fileName, setFileName] = useState('');
  const simpleValidator = useRef(new SimpleReactValidator());

  const uploadButton = (
    <div>
      {loader ? <Icon type="loading" /> : <Uploadicon />}
      <div className="ant-upload-text mt-2">Click or drag the file here to upload</div>
      <div className="mt-2"><SpText color="rgba(0, 0, 0, 0.45)">Support extension：.pdf .jpg .png</SpText></div>
    </div>
  );

  const beforeUpload = (file) => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    const PDF = 'application/pdf';
    const IMAGE = 'image/*';
    const pattern = file.type.match(IMAGE) || file.type.match(PDF);
    if (!pattern) {
      message.error('File is not image or pdf');
      return false;
    }
    if (pattern && !isLt5M) {
      message.error('File must smaller than 5MB!');
      return false;
    }
    return true;
  };

  function getBase64(img, callback) {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  const fileReader = async file => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsArrayBuffer(file);
  });

  const fileUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    try {
      setLoading(true);
      const file = info.file.originFileObj;
      if (file) {
        const { data: { entity } } = await axiosAuthInstance.get(`${API_END_POINTS.UPLOAD_KIOSK}`);
        await axiosEmptyInstance.put(entity, await fileReader(file), { headers: { 'Content-Type': file.type } });
        const { data: { entity: { fileUrl } } } = await axiosAuthInstance.post(`${API_END_POINTS.UPLOAD_KIOSK}`, { fileUrl: entity });
        setBgLogo(fileUrl);
        customizeInfo.bgLogo = fileUrl;
        const pattern = /image*/;
        if (file.type.match(pattern)) {
          getBase64(file, imageUrl => {
            setPreview(<img src={imageUrl} alt="..." style={{ width: '115px' }} />);
          });
        } else {
          setPreview(<Previewicon />);
        }
        setFileName(info.file.name);
      }
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: MESSAGES.FILE_UPLOAD.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const formValid = simpleValidator.current.allValid();
      if (!formValid) {
        simpleValidator.current.showMessages();
        forceUpdate(1);
        return false;
      }
      customizeInfo.heroText = heroText;
      customizeInfo.subText = subText;
      customizeInfo.btnText = btnText;
      customizeInfo.bgLogo = bgLogo;
      return true;
    },
  }));

  return (
    <>
      <div>
        <Row>
          <Col span={12}>
            <Row className="mt-3">
              <Col>
                <SpH5>Hero Text</SpH5>
                <Input
                  type="text"
                  maxLength={10}
                  value={heroText}
                  onChange={(e) => { setHeroText(e.currentTarget.value); customizeInfo.heroText = e.currentTarget.value; }}
                />
                <SpError>
                  {simpleValidator.current.message('hero text', heroText, 'required')}
                </SpError>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <SpH5>Hero Sub Text</SpH5>
                <Input
                  type="text"
                  maxLength={50}
                  value={subText}
                  onChange={(e) => { setSubText(e.currentTarget.value); customizeInfo.subText = e.currentTarget.value; }}
                />
                <SpError>
                  {simpleValidator.current.message('hero sub text', subText, 'required')}
                </SpError>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <SpH5>Primary Btn (CTA)</SpH5>
                <Input
                  type="text"
                  value={btnText}
                  onChange={(e) => { setBtnText(e.currentTarget.value); customizeInfo.btnText = e.currentTarget.value; }}
                />
                <SpError>
                  {simpleValidator.current.message('primary btn', btnText, 'required')}
                </SpError>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <label htmlFor=""><SpH5>Background Image <span style={{ color: 'red' }}>*</span></SpH5></label>
                <Upload
                  name="avatar"
                  listType="picture-card upload-card"
                  className="avatar-uploader"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={fileUpload}
                >
                  {preview || uploadButton}
                </Upload>
                <SpError>
                  {simpleValidator.current.message('logo', bgLogo, 'required')}
                </SpError>
              </Col>
            </Row>
          </Col>
          <Col span={4} className="ml-5">
            <div style={{ boxShadow: '5px 5px 10px grey', height: '300px' }}>
              <div style={{ backgroundColor: brandingInfo?.themeColor ? brandingInfo?.themeColor : '#007bff' }}>
                <Row type="flex" justify="space-between">
                  <Col>
                    &nbsp;
                  </Col>
                  <Col>
                    <SpText fontSize="6px" className="mr-2" color={brandingInfo?.themeColor ? brandingInfo?.themeColor : '#FFF'}>
                      I accept only card payments
                    </SpText>
                  </Col>
                </Row>
              </div>
              <div style={{ height: '50px' }}>
                <Row type="flex" justify="space-between" className="mt-2">
                  <Col span={6}>
                    {
                      brandingInfo?.logo ? (
                        <img src={brandingInfo?.logo} width="50" alt="logo" />
                      ) : (
                        <>&nbsp;</>
                      )
                    }
                  </Col>
                  <Col span={18}>
                    <div>
                      <SpText fontSize="16px" className="mr-2">
                        {brandingInfo?.shopName || 'Shop Name'}
                      </SpText>
                    </div>
                    <div>
                      <SpText fontSize="12px" className="mr-2">
                        {brandingInfo?.shopAddress || 'Address'}
                      </SpText>
                    </div>
                  </Col>
                </Row>
              </div>
              <div style={{
                height: '185px',
                backgroundImage: bgLogo ? `url(${bgLogo})` : '',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
              >
                <Row type="flex" justify="center" align="middle" className="mt-3">
                  <Col style={{ textAlign: 'center', alignItems: 'center' }}>
                    <div>
                      <SpText fontSize="20px" className="mr-2">
                        {heroText || 'Hero Text'}
                      </SpText>
                    </div>
                    <div>
                      <SpText fontSize="16px" className="mr-2">
                        {subText || 'Hero Sub Text'}
                      </SpText>
                    </div>
                    <div>
                      <Button>
                        {btnText || 'Primary Btn (CTA)'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
              <div style={{ backgroundColor: brandingInfo?.themeColor ? brandingInfo?.themeColor : '#007bff' }}>
                <Row type="flex" justify="center" align="middle">
                  <Col style={{ textAlign: 'center', alignItems: 'center' }}>
                    <SpText fontSize="6px" className="mr-2" color={brandingInfo?.themeColor ? brandingInfo?.themeColor : '#FFF'}>
                      SWIREPAY
                    </SpText>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
});

const mapDispatchToProps = (dispatch) => ({
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(
  null,
  mapDispatchToProps,
  null,
  { forwardRef: true },
)(Customize);
